import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import { useForm } from "react-hook-form";
import { number, object, preprocess, string, infer as infer_ } from "zod";

import { NumberInputFormField } from "src/components/Form/NumberInputFormField.tsx";
import { TextAreaFormField } from "src/components/Form/TextAreaFormField.tsx";
import { TextInputFormField } from "src/components/Form/TextInputFormField.tsx";
import { useGetScenarioNamePlaceholderExample } from "src/hooks/scenario";
import { useT } from "src/internationalization";
import { ShortScenario } from "src/types/scenario.ts";

export type CreateScenarioVersionFormRef = {
  submit: (onSubmit: (data: ValidatedFormState) => void, onError: () => void) => () => void;
};

export type CreateScenarioVersionFormProps = {
  scenario: ShortScenario;
  onValidChange: (isValid: boolean) => void;
};

type FormState = {
  name: string;
  threshold: string;
  description: string;
};

type ValidatedFormState = infer_<ReturnType<typeof useSchema>>;

export const CreateScenarioVersionForm = forwardRef<
  CreateScenarioVersionFormRef,
  CreateScenarioVersionFormProps
>(function CreateScenarioVersionForm({ onValidChange, scenario }, ref) {
  const t = useT();
  const validationSchema = useSchema();
  const getNamePlaceholderExample = useGetScenarioNamePlaceholderExample();

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitted },
  } = useForm<FormState>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: "",
      threshold: scenario.threshold.toString(),
      description: "",
    },
  });

  useEffect(() => {
    onValidChange(isSubmitted ? isValid : true);
    // We do not want to fire this effect when onValidChange changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  useImperativeHandle(ref, () => {
    return {
      submit: (onSubmit, onError) =>
        handleSubmit((data) => {
          const validatedData = data as unknown as ValidatedFormState;
          onSubmit(validatedData);
        }, onError),
    };
  });

  return (
    <div className="flex flex-col gap-7">
      <div className="text-textPrimary text-xl">
        {t("create.scenario.version.modal.version.tab.title")}
      </div>
      <div className="flex flex-col gap-5">
        <TextInputFormField
          id="versionName"
          control={control}
          name="name"
          placeholder={t("general.form.placeholder.example", {
            example: getNamePlaceholderExample(),
          })}
          label={t("general.form.name.label")}
          helperText={t("scenarios.version.form.name.helper")}
        />
        <NumberInputFormField
          id="versionThreshold"
          control={control}
          name="threshold"
          placeholder={t("general.form.placeholder.example", { example: "100" })}
          label={t("scenarios.form.threshold.label")}
          helperText={t("scenarios.form.threshold.helper")}
        />
        <TextAreaFormField
          id="versionDescription"
          control={control}
          name="description"
          label={t("general.form.description.label")}
          helperText={t("scenarios.version.form.description.helper")}
        />
      </div>
    </div>
  );
});

const useSchema = () => {
  const t = useT();

  return useMemo(() => {
    return object({
      name: string().min(1, t("general.form.name.error.required")),
      threshold: preprocess(
        (value) => {
          if (typeof value === "string") {
            const parsed = parseFloat(value);
            return isNaN(parsed) ? undefined : parsed;
          }
          return value;
        },
        number({
          message: t("scenarios.form.threshold.error.required"),
        }).min(0, t("scenarios.form.threshold.error.min", { min: 0 }))
      ),
      description: string().min(1, t("general.form.description.error.required")),
    });
  }, [t]);
};
